import React, { Component } from 'react';
import { toast } from 'react-toastify';
import {
  WorkspaceChrome,
  WorkspaceSummary,
  WorkspaceActions,
  WorkspaceSection,
  WorkspaceStat,
  tabFromQuery,
} from '../../../platform/workspace360';
import { getInvoice, downloadInvoicePdf } from '../../../services/invoiceService';
import InvoicePayModal from '../InvoicePayModal';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'lines', label: 'Line items' },
];

function formatMoney(value) {
  const amount = Number(value || 0);
  return Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);
}

class Invoice360Panel extends Component {
  state = {
    invoice: null,
    loading: true,
    activeTab: 'overview',
    paying: false,
  };

  componentDidMount() {
    this.syncTab();
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.load();
    }
    if (prevProps.location?.search !== this.props.location?.search) {
      this.syncTab();
    }
  }

  syncTab = () => {
    this.setState({
      activeTab: tabFromQuery(this.props.location?.search, 'overview', TABS),
    });
  };

  load = async () => {
    this.setState({ loading: true });
    try {
      const invoiceRes = await getInvoice(this.props.match.params.id);
      const invoice = invoiceRes?.data?.data || invoiceRes?.data;
      this.setState({
        invoice,
        loading: false,
      });
    } catch (ex) {
      this.setState({ loading: false });
      if (ex.response && ex.response.status === 404) {
        this.props.history.replace('/not-found');
      } else {
        toast.error('Failed to load invoice.');
      }
    }
  };

  handleTabChange = (tabId) => {
    this.setState({ activeTab: tabId });
    const search = tabId === 'overview' ? '' : `?tab=${tabId}`;
    this.props.history.replace({ pathname: this.props.location.pathname, search });
  };

  handleDownload = async () => {
    const { invoice } = this.state;
    try {
      await downloadInvoicePdf(invoice.id, `invoice-${invoice.invoicenum}.pdf`);
    } catch (_) {
      toast.error('Failed to download invoice PDF.');
    }
  };

  renderOverview() {
    const { invoice } = this.state;
    return (
      <WorkspaceSection title="Overview" icon="fa-file-text-o">
        <div className="customer360-grid-3">
          <WorkspaceStat label="Invoice #" value={invoice.invoicenum} isStatic />
          <WorkspaceStat label="Status" value={invoice.status} isStatic />
          <WorkspaceStat
            label="Type"
            value={invoice.obligationLabel || 'Invoice settlement'}
            isStatic
          />
          <WorkspaceStat label="Total" value={formatMoney(invoice.total)} isStatic />
          <WorkspaceStat label="Balance" value={formatMoney(invoice.balance)} isStatic />
          <WorkspaceStat label="Paid" value={formatMoney(invoice.amount_paid)} isStatic />
          <WorkspaceStat label="Issued" value={invoice.issuedate} isStatic />
          <WorkspaceStat label="Due" value={invoice.duedate} isStatic />
          <WorkspaceStat label="Terms" value={invoice.payment_terms} isStatic />
        </div>
      </WorkspaceSection>
    );
  }

  renderLines() {
    const items = this.state.invoice.items || [];
    return (
      <WorkspaceSection title="Line items" icon="fa-list">
        {!items.length && <p className="customer360-muted mb-0">No line items.</p>}
        {!!items.length && (
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {(item.catalogItem && item.catalogItem.name) ||
                        item.description ||
                        (item.catalogItem && item.catalogItem.item_code) ||
                        `#${item.id}`}
                    </td>
                    <td>{item.quantity != null ? item.quantity : '—'}</td>
                    <td>{formatMoney(item.amount != null ? item.amount : item.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </WorkspaceSection>
    );
  }

  render() {
    const {
      invoice,
      loading,
      activeTab,
      paying,
    } = this.state;
    if (loading) {
      return (
        <div className="customer360-view customer360-console py-3 customer360-muted">
          <i className="animate-spin fa fa-spinner" /> Loading invoice…
        </div>
      );
    }
    if (!invoice) return null;

    const canPay = Number(invoice.balance) > 0;
    const summary = (
      <WorkspaceSummary
        title="Invoice Overview"
        icon="fa-file-text-o"
        stats={[
          {
            key: 'status',
            label: 'Status',
            value: invoice.status || '—',
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
          {
            key: 'balance',
            label: 'Balance',
            value: formatMoney(invoice.balance),
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
          {
            key: 'total',
            label: 'Total',
            value: formatMoney(invoice.total),
            onClick: () => this.handleTabChange('lines'),
            isActive: activeTab === 'lines',
          },
        ]}
      />
    );

    const actions = (
      <WorkspaceActions>
        {canPay ? (
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={() => this.setState({ paying: true })}
          >
            <i className="fa fa-credit-card mr-1" aria-hidden /> Pay now
          </button>
        ) : null}
        <button type="button" className="btn btn-light btn-sm" onClick={this.handleDownload}>
          <i className="fa fa-file-pdf-o mr-1" aria-hidden /> PDF
        </button>
      </WorkspaceActions>
    );

    return (
      <React.Fragment>
        <WorkspaceChrome
          title={invoice.invoicenum || `Invoice #${invoice.id}`}
          subtitle={[invoice.status, formatMoney(invoice.balance)].filter(Boolean).join(' · ')}
          listLabel="Invoices"
          listTo="/invoices"
          recordLabel={invoice.invoicenum || `Invoice #${invoice.id}`}
          summary={summary}
          actions={actions}
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={this.handleTabChange}
        >
          {activeTab === 'lines' ? this.renderLines() : this.renderOverview()}
        </WorkspaceChrome>
        {paying && (
          <InvoicePayModal
            invoice={invoice}
            onClose={() => this.setState({ paying: false })}
            onPaid={() => {
              this.setState({ paying: false });
              this.load();
            }}
          />
        )}
      </React.Fragment>
    );
  }
}

export default Invoice360Panel;
